import React, { useState } from 'react';
import type { ToolFilterCondition } from '@troia-v3/types';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { FormField } from '@/shared/components/ui/FormField';
import {
  OPERATORS,
  OPERATORS_WITHOUT_VALUE,
  getFieldsByCategory,
  isJsonPathField,
  JSON_FIELD_SENTINEL,
} from '../filters/ToolFilterDefinitions';

// ---------------------------------------------------------------------------
// Tipos dos sub-itens editados via modal (headers, parâmetros, filtros).
// ---------------------------------------------------------------------------
export type SubItemKind = 'header' | 'param' | 'aiparam' | 'filter';

export interface HeaderDraft {
  key: string;
  value: string;
}

export interface ParamDraft {
  name: string;
  type: string;
  description: string;
  required: boolean;
}

export type FilterDraft = ToolFilterCondition;

export type SubItemValue = HeaderDraft | ParamDraft | FilterDraft;

const PARAM_TYPES = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
];
const AI_PARAM_TYPES = PARAM_TYPES.slice(0, 3);

const KIND_LABELS: Record<SubItemKind, string> = {
  header: 'Header',
  param: 'Parâmetro',
  aiparam: 'Parâmetro de IA',
  filter: 'Filtro de Ativação',
};

interface SubItemModalProps {
  kind: SubItemKind;
  /** Valor existente ao editar; `null` ao adicionar. */
  initial: SubItemValue | null;
  onClose: () => void;
  onSave: (value: SubItemValue) => void;
}

/**
 * Modal (tema claro fixo — via Modal shared) para adicionar/editar um sub-item
 * do formulário de ferramenta. Substitui a edição inline: cada header, parâmetro
 * e filtro é editado por um botão → modal (regra de sub-itens do Design System).
 */
export const SubItemModal: React.FC<SubItemModalProps> = ({ kind, initial, onClose, onSave }) => {
  const [draft, setDraft] = useState<SubItemValue>(() => {
    if (initial) return initial;
    if (kind === 'header') return { key: '', value: '' } as HeaderDraft;
    if (kind === 'filter') return { field: '', operator: 'equals', value: '' } as FilterDraft;
    return { name: '', type: 'string', description: '', required: false } as ParamDraft;
  });

  const title = `${initial ? 'Editar ' : 'Adicionar '}${KIND_LABELS[kind]}`;

  // ---- Validação (habilita o botão Salvar) ---------------------------------
  let valid = false;
  if (kind === 'header') {
    const d = draft as HeaderDraft;
    valid = d.key.trim() !== '' && d.value.trim() !== '';
  } else if (kind === 'filter') {
    const d = draft as FilterDraft;
    valid = d.field.trim() !== '' && !(isJsonPathField(d.field) && d.field.trim() === '$.');
  } else {
    const d = draft as ParamDraft;
    valid = d.name.trim() !== '';
  }

  // ---- Corpo do modal por tipo ---------------------------------------------
  let body: React.ReactNode = null;

  if (kind === 'header') {
    const d = draft as HeaderDraft;
    body = (
      <>
        <FormField label="Nome do Header" required>
          <Input
            className="font-mono"
            value={d.key}
            onChange={(e) => setDraft({ ...d, key: e.target.value })}
            placeholder="Ex: Authorization"
            data-testid="subitem-header-key"
          />
        </FormField>
        <FormField label="Valor" required>
          <Input
            className="font-mono"
            value={d.value}
            onChange={(e) => setDraft({ ...d, value: e.target.value })}
            placeholder="Ex: Bearer {{secret}}"
            data-testid="subitem-header-value"
          />
        </FormField>
      </>
    );
  } else if (kind === 'param' || kind === 'aiparam') {
    const d = draft as ParamDraft;
    body = (
      <>
        <FormField
          label="Nome"
          required
          description="Nome usado na interpolação {parameterName}"
        >
          <Input
            className="font-mono"
            value={d.name}
            onChange={(e) => setDraft({ ...d, name: e.target.value })}
            placeholder="Ex: document"
            data-testid="subitem-param-name"
          />
        </FormField>
        <FormField label="Tipo" required>
          <Select
            options={kind === 'param' ? PARAM_TYPES : AI_PARAM_TYPES}
            value={d.type}
            onChange={(v) => setDraft({ ...d, type: v })}
            data-testid="subitem-param-type"
          />
        </FormField>
        <FormField label="Descrição">
          <Input
            value={d.description}
            onChange={(e) => setDraft({ ...d, description: e.target.value })}
            placeholder="Descreva a entrada para o agente"
            data-testid="subitem-param-description"
          />
        </FormField>
        <Checkbox
          label="Obrigatório"
          checked={d.required}
          onChange={(c) => setDraft({ ...d, required: c })}
          data-testid="subitem-param-required"
        />
      </>
    );
  } else {
    const d = draft as FilterDraft;
    const isJson = isJsonPathField(d.field);
    const selectValue = isJson ? JSON_FIELD_SENTINEL : d.field;
    const hideValue = OPERATORS_WITHOUT_VALUE.includes(d.operator);
    const fieldGroups = Object.entries(getFieldsByCategory()).map(([label, fields]) => ({
      label,
      options: fields.map((f) => ({ value: f.value, label: f.label })),
    }));

    const handleFieldSelect = (value: string): void => {
      setDraft({ ...d, field: value === JSON_FIELD_SENTINEL ? '$.' : value });
    };

    body = (
      <>
        <FormField label="Campo" required>
          <Select
            placeholder="Selecionar campo..."
            groups={fieldGroups}
            value={selectValue}
            onChange={handleFieldSelect}
            data-testid="subitem-filter-field"
          />
          {isJson && (
            <div className="mt-2">
              <Input
                className="font-mono"
                value={d.field}
                onChange={(e) => setDraft({ ...d, field: e.target.value })}
                placeholder="$.cliente.status"
                data-testid="subitem-filter-json-path"
              />
              <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                Caminho no JSON retornado pela requisição. Ex: $.status ou $.cliente.status
              </p>
            </div>
          )}
        </FormField>
        <FormField label="Operador" required>
          <Select
            options={OPERATORS.map((op) => ({ value: op.value, label: op.label }))}
            value={d.operator}
            onChange={(v) => setDraft({ ...d, operator: v })}
            data-testid="subitem-filter-operator"
          />
        </FormField>
        <FormField label="Valor">
          <Input
            value={hideValue ? '' : d.value}
            onChange={(e) => setDraft({ ...d, value: e.target.value })}
            disabled={hideValue}
            placeholder={hideValue ? 'Sem valor para este operador' : 'Ex: active'}
            data-testid="subitem-filter-value"
          />
        </FormField>
      </>
    );
  }

  return (
    <Modal isOpen onClose={onClose} maxWidth="lg">
      <ModalHeader onClose={onClose}>
        <h2 className="text-base font-semibold" style={{ color: 'var(--theme-card-text)' }}>
          {title}
        </h2>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4">{body}</div>
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} data-testid="subitem-modal-cancel">
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={() => onSave(draft)}
          disabled={!valid}
          data-testid="subitem-modal-save"
        >
          Salvar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SubItemModal.displayName = 'SubItemModal';
